/**
 * Where a hint bubble goes relative to its target, and where its arrow points.
 *
 * Pure geometry with no Angular and no DOM in it, so it can be unit-tested. The component that
 * feeds it the rects and writes the styles is components/hint-bubble/hint-bubble.ts.
 */

export type HintBubblePosition = 'above' | 'below';

/** the parts of a DOMRect this needs */
export interface HintRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface HintBubblePlacement {
  /** how far to move the bubble horizontally from where it was first laid out */
  leftDelta: number;
  /** how far to move the bubble vertically from where it was first laid out */
  topDelta: number;
  /** the arrow's left offset inside the bubble, so it points at the target's centre */
  arrowX: number;
}

// how close the bubble may come to either side of the screen
const SCREEN_EDGE_MARGIN = 10;
// the arrow is drawn this far right of where its left offset puts it
const ARROW_OFFSET = 10;
// the gap left between the target and the bubble, which differs by side because of the arrow
const GAP_ABOVE = 15;
const GAP_BELOW = 5;

/**
 * Centre the bubble on the target, keep it on screen, point the arrow at the target's centre and
 * put the bubble above or below the target.
 *
 * `bubbleRect` is the bubble's rect as first laid out, before any offset was applied; the deltas
 * returned are relative to it. The bubble is assumed never to be wider than the screen.
 */
export function placeHintBubble(
  targetRect: HintRect,
  bubbleRect: HintRect,
  viewportWidth: number,
  position: HintBubblePosition,
): HintBubblePlacement {
  let left = bubbleRect.left;
  let right = bubbleRect.right;

  // horizontally, start by centering to target
  const targetCenterX = (targetRect.left + targetRect.right) / 2;
  const centerDelta = targetCenterX - (left + right) / 2;
  left += centerDelta;
  right += centerDelta;

  // now clamp so we don't get out of screen (with a small margin)
  let clampDelta = 0;
  if (left < SCREEN_EDGE_MARGIN) {
    clampDelta = SCREEN_EDGE_MARGIN - left;
  } else if (right > viewportWidth - SCREEN_EDGE_MARGIN) {
    clampDelta = viewportWidth - SCREEN_EDGE_MARGIN - right;
  }
  left += clampDelta;

  // set the arrow to point straight at the target center
  const arrowX = targetCenterX - (left + ARROW_OFFSET);

  const leftDelta = left - bubbleRect.left;
  const topDelta = position === 'above'
    ? targetRect.top - bubbleRect.bottom - GAP_ABOVE
    : targetRect.bottom - bubbleRect.top + GAP_BELOW;

  return { leftDelta, topDelta, arrowX };
}
